import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Empty,
  Input,
  InputRef,
  Menu,
  Popconfirm,
  Space,
  Table,
  TablePaginationConfig,
  Tree,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import {
  CopyOutlined,
  DeleteOutlined,
  FilterFilled,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { FilterConfirmProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { DataNode, TreeProps } from "antd/lib/tree";
import { isConditionalExpression } from "typescript";
import "./Datatable.css";
import { useTranslation } from "react-i18next";
import FieldMappingsService from "../../../services/fieldmappings/FieldMappingsService";
import { useDispatch, useSelector } from "react-redux";
import { setRecordsPerPage } from "../../../redux/actions";
import moment, {isMoment} from "moment";
import isNumeric from "antd/es/_util/isNumeric";

const { Search } = Input;

const Datatable = ({
  tableColumns,
  data,
  setData,
  isTableLoading,
  scroll,
  otherActions = [],
  otherRowEnabled = false,
  editRowEnabled = false,
  copyRowEnabled = false,
  deleteRowEnabled = undefined,
  viewRowEnabled = undefined,
  onEditFunc = undefined,
  onCopyFunc = undefined,
  onDeleteFunc = undefined,
  onViewFunc = undefined,
  onCustomActionItem = undefined,
  onDoubleClickRow = undefined,
  selectable = false,
  multiSelect = false,
  selectedRows = undefined,
  setSelectedRows = undefined,
  paginationDisabled = false,
  paginationVisible = true,
  paginationPageIndex = undefined,
  paginationOnChange = undefined,
  paginationPageSize = undefined,
  showActionsColumn = true,
  defaultSortColumn = undefined,
  defaultSortColumnDirection = undefined,
  checkPermissions = false,
  getPermissionsFunction = undefined,
  sticky = false,
}: {
  tableColumns: any;
  data: any;
  setData: any;
  scroll: any;
  isTableLoading: any;
  otherActions?: any;
  otherRowEnabled?: boolean;
  editRowEnabled?: boolean;
  copyRowEnabled?: boolean;
  deleteRowEnabled?: boolean;
  viewRowEnabled?: boolean;
  onEditFunc?: any;
  onCopyFunc?: any;
  onDeleteFunc?: any;
  onViewFunc?: any;
  onCustomActionItem?: any,
  onDoubleClickRow?: any;
  selectable?: boolean;
  multiSelect?: boolean;
  selectedRows?: any;
  setSelectedRows?: any;
  paginationDisabled?: boolean;
  paginationVisible?: boolean;
  paginationPageIndex?: any;
  paginationPageSize?: number;
  paginationOnChange?: any;
  showActionsColumn?: boolean;
  defaultSortColumn?: string;
  defaultSortColumnDirection?: string;
  checkPermissions?: boolean;
  getPermissionsFunction?: any;
  sticky?: any;
}) => {
  const { t, i18n } = useTranslation("translations");
  let locale = {
    emptyText: [
      <Empty
        description={t("General.Labels.NoData", {
          lngs: i18n.languages,
          defaultValue: "No data to display",
        })}
        image={Empty.PRESENTED_IMAGE_DEFAULT}
      />,
    ],
  };

  const { recordsPerPage } = useSelector((state: any) => ({
    recordsPerPage: state.recordsPerPage,
  }));

  const { permissions, groupCode, entity } = useSelector((state: any) => ({
    permissions: state.permissions,
    groupCode: state.groupCode,
    entity: state.entity,
  }));

  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInput = useRef<InputRef>(null);

  const [searchValues, setSearchValues] = useState<any>({});
  const [filters, setFilters] = useState<any>({});
  const [checkedKeys, setCheckedKeys] = useState<any>({});
  const [ascendingProp, setAscendingProp] = useState<string>("");
  const [descendingProp, setDescendingProp] = useState<string>("");

  const [defaultSortSet, setDefaultSortSet] = useState<boolean>(false);

  const [columns, setColumns] = useState<ColumnType<any>[]>([]);

  const [filterTreeData, setFilterTreeData] = useState<any>({});

  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);

  const [initialLoad, setInitialLoad] = useState<boolean>(false);

  const { userHasPermissions } = useSelector((state: any) => ({
    userHasPermissions: state.userHasPermissions,
  }));

  const paginationConfig: TablePaginationConfig = {
    style: !paginationVisible ? { display: "none" } : {},
    ...(paginationPageSize
      ? { pageSize: paginationPageSize }
      : { pageSize: recordsPerPage }),
    defaultPageSize: recordsPerPage,
    showQuickJumper: true,
    showSizeChanger: true,
    locale: {
      items_per_page: t("General.Table.Pagination.ItemPerPage", {
        lngs: i18n.languages,
        defaultValue: "/page",
      })!,
      page: t("General.Table.Pagination.Page", {
        lngs: i18n.languages,
        defaultValue: "page",
      })!,
      jump_to: t("General.Table.Pagination.GoTo", {
        lngs: i18n.languages,
        defaultValue: "Go to",
      })!,
    },
    showTotal: (total: number, range: [number, number]) => {
      const translation = t("General.Table.Pagination.RangeRecords", {
        lngs: i18n.languages,
        defaultValue: `${range[0]}-${range[1]} out of ${total} records`,
      });
      return `${translation
        .replace("${range[0]}", range[0].toString())
        .replace("${range[1]}", range[1].toString())
        .replace("${total}", total.toString())}`;
    },
    itemRender: (page: number, type, originalElement) => {
      return originalElement;
    },
    ...(paginationPageIndex && { current: paginationPageIndex }),
    ...(paginationOnChange && { onChange: paginationOnChange }),
    onChange: (page: number, pageSize: number) => {
      dispatch(setRecordsPerPage(pageSize));
    },
  };

  useEffect(() => {
    if (tableColumns) {
      let searchValues: any = {};
      let filters: any = [];
      let updateSearchValues = false;
      let updateFilters = false;
      for (let i = 0; i < tableColumns.length; i++) {
        if (!searchValues[tableColumns[i].key]) {
          searchValues[tableColumns[i].key] = "";
          updateSearchValues = true;
        }
        if (!filters[tableColumns[i].key]) {
          filters[tableColumns[i].key] = [];
          updateFilters = true;
        }
      }
      if (updateSearchValues) {
        setSearchValues(searchValues);
      }
      if (updateFilters) {
        setFilters(filters);
      }
    }
  }, [tableColumns]);

  useEffect(() => {
    if (tableColumns && data) {
      const distinctBy = (prop: any, arr: any) => [
        ...new Set(arr.map((o: any) => o[prop])),
      ];

      let filters: any = {};
      for (let i = 0; i < tableColumns.length; i++) {
        let result = distinctBy(tableColumns[i].key, data);
        filters[tableColumns[i].key] = result.map((value: any, index: any) => ({
          key: index,
          title: value ?? "",
        }));
      }
      setFilters(filters);
    }
  }, [tableColumns, data]);

  useEffect(() => {
    if (!initialLoad && tableColumns && filters) {
      for (let i = 0; i < tableColumns.length; i++) {
        setCheckedKeys((prevState: any) => ({
          ...prevState,
          [tableColumns[i].key]: filters[tableColumns[i].key]?.map(
            (x: any) => x.key
          ),
        }));
      }
    }
  }, [filters, tableColumns]);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm();
    if (searchValues[dataIndex]) {
      setSearchText(searchValues[dataIndex]);
    }
    setSearchedColumn(dataIndex);
    setInitialLoad(true);
  };

  const handleReset = (clearFilters: () => void, dataIndex: any) => {
    clearFilters();
    setSearchText("");

    if (filters) {
      setCheckedKeys((prevState: any) => ({
        ...prevState,
        [dataIndex]: filters[dataIndex]?.map(
          (x: any) => x.key
        ),
      }));
    }
  };

  const handleUnselectAll = (clearFilters: () => void, dataIndex: any) => {
    clearFilters();
    setSearchText("");
    
    setCheckedKeys((prevState: any) => ({
      ...prevState,
      [dataIndex]: [],
    }));
  };

  const onFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    const { value } = e.target;
    
    let newSearchValues = Object.assign({}, searchValues);
    newSearchValues[key] = value;
    setSearchValues(newSearchValues);
  };
  
  const isDate = (item: any) => {
    if (isMoment(item)) {
      return true;
    }
    if (moment(item, "DD/MM/YYYY HH:mm:ss", true).isValid()) {
      return true;
    }
    if (moment(item, "YYYY-MM-DDTHH:mm:ss", true).isValid()) {
      return true;
    }
    return false;
  }

  const convertDate = (item: any) => {
    if (isMoment(item)) {
      return item;
    }
    if (moment(item, "DD/MM/YYYY HH:mm:ss", true).isValid()) {
      return moment(item, "DD/MM/YYYY HH:mm:ss", true)
    }
    if (moment(item, "YYYY-MM-DDTHH:mm:ss", true).isValid()) {
      return moment(item, "YYYY-MM-DDTHH:mm:ss", true);
    }
  }

  useEffect(() => {
    if (filters && searchValues) {
      const loop = (key: string, data: any): any =>
        data
          ?.map((item: any) => {
            // const strTitle = typeof(item.title) == "boolean" ? item.title.toString().toUpperCase() : (item.title ? item.title.toString() : "");
            const strTitle = item.title ? item.title.toString() : "";
            const index = strTitle
              .toLowerCase()
              .indexOf(searchValues[key].toLowerCase());
            const beforeStr = strTitle.substring(0, index);
            const afterStr = strTitle.slice(index + searchValues[key].length);
            const title =
              index > -1 ? (
                <span>
                  {beforeStr}
                  <span className="site-tree-search-value">
                    {searchValues[key]}
                  </span>
                  {afterStr}
                </span>
              ) : (
                <span>{strTitle}</span>
              );

            return {
              title,
              key: item.key,
              shouldRemove: index === -1,
            };
          })
          .filter((x: any) => !x.shouldRemove);

      let newData: any = {};
      for (let i = 0; i < tableColumns.length; i++) {
        newData[tableColumns[i].key] = loop(
          tableColumns[i].key,
          filters[tableColumns[i].key]?.map((x: any) => ({
            key: x.key,
            title: x.title ?? "",
          }))
        );

        if (searchValues[tableColumns[i].key]) {
        }
      }
      setFilterTreeData(newData);
    }
  }, [filters, searchValues]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (ascendingProp) {
        setData([
          ...newTableItems.sort((a, b) => {
            const column = columns.find(x => x.key === ascendingProp);
            if (typeof column?.sorter === 'function') {
              return column.sorter(a, b);
            }
            if (isDate(a[ascendingProp]) && isDate(b[ascendingProp])) {
              return convertDate(a[ascendingProp]).unix() - convertDate(b[ascendingProp]).unix();
            }
            if (isNumeric(a[ascendingProp]) && isNumeric(b[ascendingProp])) {
              return a[ascendingProp] - b[ascendingProp];
            }
            return a[ascendingProp]?.trim().localeCompare(b[ascendingProp]?.trim());
          }),
        ]);
      }
      if (descendingProp) {
        setData([
          ...newTableItems.sort((a, b) => {
            const column = columns.find(x => x.key === descendingProp);
            if (typeof column?.sorter === 'function') {
              return column.sorter(b, a);
            }
            if (isDate(a[descendingProp]) && isDate(b[descendingProp])) {
              return convertDate(b[descendingProp]).unix() - convertDate(a[descendingProp]).unix();
            }
            if (isNumeric(a[descendingProp]) && isNumeric(b[descendingProp])) {
              return b[descendingProp] - a[descendingProp];
            }
            return b[descendingProp]?.trim().localeCompare(a[descendingProp]?.trim());
          }),
        ]);
      }
    }
  }, [ascendingProp, descendingProp]);

  useEffect(() => {
    if (data && data.length > 0 && defaultSortColumn && !defaultSortSet) {
      if (defaultSortColumnDirection == "descend") {
        setDescendingProp(defaultSortColumn);
      } else {
        setAscendingProp(defaultSortColumn);
      }
      setDefaultSortSet(true);
    }
  }, [data, defaultSortColumn, defaultSortColumnDirection]);

  const onTreeItemCheck = (
    keys: any,
    info: any,
    column: any,
    selectedKeys: any,
    setSelectedKeys: any,
    clearFilters: any
  ) => {
    setCheckedKeys((prevState: any) => ({ ...prevState, [column]: keys }));
    if (keys.length > 0) {
      setSelectedKeys([
        ...filters[column]
          .filter((x: any) => keys.includes(x.key))
          .map((x: any) => x.title),
      ]);
    } else {
      setSelectedKeys([
        ...selectedKeys,
        ...filters[column].map((x: any) => x.title),
      ]);
      clearFilters();
    }
  };

  const getColumnSearchProps = (
    dataIndex: string,
    filterChangeFunc: any,
    data: any,
    checkedKeys: any,
    onTreeItemCheck: any,
    itemsAcending: any,
    itemsDescending: any,
    setItemsAscending: any,
    setItemsDescending: any
  ): ColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        className="tableFilter"
        style={{ padding: "4px 10px", maxWidth: "300px", height: "400px" }}
      >
        <div className="sortingContainer">
          <span>
            {t("General.Labels.Sort", {
              lngs: i18n.languages,
              defaultValue: "Sort",
            })}
          </span>
          <Button
            type="text"
            icon={<ArrowUpOutlined />}
            style={{
              textAlign: "left",
              paddingLeft: "8px",
              backgroundColor: itemsAcending === dataIndex ? "#7BBDB1" : "",
              color: itemsAcending === dataIndex ? "#fff" : "",
            }}
            onClick={() => {
              setItemsAscending(dataIndex);
              setItemsDescending(undefined);
            }}
          >
            {t("General.Labels.Ascending", {
              lngs: i18n.languages,
              defaultValue: "Ascending",
            })}
          </Button>
          <Button
            type="text"
            icon={<ArrowDownOutlined />}
            style={{
              textAlign: "left",
              paddingLeft: "8px",
              backgroundColor: itemsDescending === dataIndex ? "#7BBDB1" : "",
              color: itemsDescending === dataIndex ? "#fff" : "",
            }}
            onClick={() => {
              setItemsDescending(dataIndex);
              setItemsAscending(undefined);
            }}
          >
            {t("General.Labels.Descending", {
              lngs: i18n.languages,
              defaultValue: "Descending",
            })}
          </Button>
        </div>
        <Divider />
        <Search
          prefix={<SearchOutlined />}
          allowClear
          ref={searchInput}
          placeholder={
            t("General.Placeholders.EnterValue", {
              lngs: i18n.languages,
              defaultValue: "Enter value",
            })!
          }
          value={searchValues[dataIndex]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            filterChangeFunc(e, dataIndex);
            if (checkedKeys[dataIndex]) {
              if (e.target.value) {
                const newCheckedKeys = {
                  ...checkedKeys,
                  [dataIndex]: filters[dataIndex]
                    .filter((x: any) =>
                      x.title
                        .toLowerCase()
                        .includes(e.target.value.toLowerCase())
                    )
                    .map((x: any) => x.key),
                };
                setCheckedKeys(newCheckedKeys);
                setSelectedKeys([
                  ...filters[dataIndex]
                    .filter((x: any) =>
                      newCheckedKeys[dataIndex].includes(x.key)
                    )
                    .map((x: any) => x.title),
                  e.target.value,
                ]);
              } else {
                setSelectedKeys([
                  ...filters[dataIndex]
                    .filter((x: any) => checkedKeys[dataIndex].includes(x.key))
                    .map((x: any) => x.title),
                ]);
              }
            } else {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }
          }}
          onPressEnter={() => {
            handleSearch(selectedKeys as string[], confirm, dataIndex);
          }}
          style={{ marginBottom: 8, display: "flex" }}
        />
        <Tree
          checkable
          onCheck={(key, info) =>
            onTreeItemCheck(
              key,
              info,
              dataIndex,
              selectedKeys,
              setSelectedKeys,
              clearFilters
            )
          }
          checkedKeys={checkedKeys[dataIndex]}
          treeData={data[dataIndex]}
          style={{ height: "200px", overflowY: "auto", scrollbarWidth: "thin" }}
        />
        <Divider />
        <div className="filterButtons">
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            type="text"
          >
            {
              t("General.Labels.Reset", {
                lngs: i18n.languages,
                defaultValue: "Reset",
              })!
            }
          </Button>
          <Button
            onClick={() => clearFilters && handleUnselectAll(clearFilters, dataIndex)}
            size="small"
            type="text"
          >
            {
              t("General.Labels.UnselectAll", {
                lngs: i18n.languages,
                defaultValue: "Unselect all",
              })!
            }
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
          >
            {t("General.Labels.Ok", {
              lngs: i18n.languages,
              defaultValue: "OK",
            })}
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      if (typeof record[dataIndex] === "boolean") {
        return record[dataIndex] === (value === "true");
      }
      if (searchValues[dataIndex] === value) {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes((value.toString()).toLowerCase());
      }
      return (
          typeof record[dataIndex] === 'string' &&
          record[dataIndex].toString().toLowerCase() === (value as string).toLowerCase()
      );
    },
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getValidPermission = (record: any) => {
    if (!getPermissionsFunction) {
      return userHasPermissions;
    }
    
    return getPermissionsFunction(record);
  }

  useEffect(() => {
    if (tableColumns && filterTreeData) {
      let newCols = [
        ...tableColumns.map((x: any) => ({
          title: x.title,
          dataIndex: x.key,
          key: x.key,
          width: x.width,
          align: x.align,
          ellipsis: x.ellipsis,
          sorter: x.sorter,
          filters: [],
          filterMode: "tree",
          filterSearch: true,
          filterTreeNode: () => false,
          ...getColumnSearchProps(
            x.key,
            onFilterChange,
            filterTreeData,
            checkedKeys,
            onTreeItemCheck,
            ascendingProp,
            descendingProp,
            setAscendingProp,
            setDescendingProp
          ),
          render: x.render
            ? x.render
            : (text: any, record: any) => {
                return (
                  <>
                    {typeof(text) == "boolean" ? (
                      <Checkbox checked={text} className={"datatable-list-item-checkbox"}></Checkbox>
                    ) : (
                      <>{text}</>
                    )}
                  </>
                );
              },
        })),
      ];

      if (showActionsColumn) {
        newCols = [
          ...newCols,
          {
            title: t("General.Table.Headers.Actions", {
              lngs: i18n.languages,
              defaultValue: "Actions",
            }),
            key: "actions",
            width: onCustomActionItem ? "100px" : "80px",
            align: "center",
            render: (_: any, record: any) => (
              <>
                {otherRowEnabled && (
                  <Space>
                    <Dropdown
                      overlay={
                        <Menu
                          onClick={(e) => {
                            const actions = otherActions(record);
                            const action = actions
                                .filter((y: any) => y !== undefined)
                                .find(
                              (y: any) => e.key === y.key
                            );
                            if (action) {
                              action.func(record);
                            }
                          }}
                          items={otherActions(record)}
                        ></Menu>
                      }
                    >
                      <MoreOutlined />
                    </Dropdown>
                  </Space>
                )}
                {editRowEnabled && (
                  <Button
                    type="text"
                    size="small"
                    onClick={() => onEditFunc(record)}
                    disabled={(checkPermissions && !getValidPermission(record)?.write)}
                    style={{ padding: "0px 2px" }}
                  >
                    <EditOutlined
                      className={
                        (checkPermissions && !getValidPermission(record)?.write)
                          ? "disabledColor"
                          : ""
                      }
                      style={{ color: "var(--primary)" }}
                    />
                  </Button>
                )}
                {(copyRowEnabled ||
                  record.copyAllowed === true ||
                  record.copyAllowed === false) &&
                  (
                  <Button
                    type="text"
                    size="small"
                    onClick={() => onCopyFunc(record)}
                    style={{ padding: "0px 2px" }}
                    disabled={(!copyRowEnabled && !record.copyAllowed) || (checkPermissions && !getValidPermission(record)?.write)}
                  >
                    <CopyOutlined
                      className={
                        (!copyRowEnabled && !record.copyAllowed) || (checkPermissions && !getValidPermission(record)?.write)
                          ? "disabledColor"
                          : ""
                      }
                      // disabled
                      style={{ color: "var(--primary)" }}
                    />
                  </Button>
                )}
                {(deleteRowEnabled ||
                  record.deleteAllowed ||
                  record.deleteAllowed === true ||
                  record.deleteAllowed === false) &&
                  (
                  <Popconfirm
                    placement="topRight"
                    title={t("General.Table.Warnings.RowDelete", {
                      lngs: i18n.languages,
                      defaultValue: "Are you sure to delete this row?",
                    })}
                    icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                    onConfirm={() => onDeleteFunc(record.id, record)}
                    okText={t("General.Actions.Yes", {
                      lngs: i18n.languages,
                      defaultValue: "Yes",
                    })}
                    cancelText={t("General.Actions.No", {
                      lngs: i18n.languages,
                      defaultValue: "No",
                    })}
                    disabled={(!deleteRowEnabled && !record.deleteAllowed) || (checkPermissions && !getValidPermission(record)?.delete)}
                  >
                    <Button
                      type="text"
                      size="small"
                      style={{ padding: "0px 2px" }}
                      disabled={(!deleteRowEnabled && !record.deleteAllowed) || (checkPermissions && !getValidPermission(record)?.delete)}
                    >
                      <DeleteOutlined
                        className={
                          (!deleteRowEnabled && !record.deleteAllowed) || (checkPermissions && !getValidPermission(record)?.delete)
                            ? "disabledColor"
                            : ""
                        }
                        style={{ color: "var(--primary)" }}
                      />
                    </Button>
                  </Popconfirm>
                )}
                {viewRowEnabled && (
                  <Button
                    type="text"
                    size="small"
                    onClick={() => onViewFunc(record)}
                    style={{ padding: "0px 2px" }}
                  >
                    <EyeOutlined style={{ color: "var(--primary)" }} />
                  </Button>
                )}
                {onCustomActionItem && onCustomActionItem(record)}
              </>
            ),
          },
        ];
      }

      setColumns([...newCols]);
    }
  }, [tableColumns, data, filterTreeData, searchText, checkedKeys]);

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    newSelectedRows: any[]
  ) => {
    if (!multiSelect) {
      const rows = newSelectedRows.filter(
        (x: any) => !selectedRows.find((y: any) => y.key === x.key)
      );
      const rowKeys = newSelectedRowKeys.filter(
        (x: any) => !selectedRows.find((y: any) => y.key === x)
      );
      if (rows.length > 0) {
        setSelectedRows([rows[0]]);
      } else {
        setSelectedRows([]);
      }
      setSelectedRowKeys([rowKeys[0]]);
    } else {
      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedRowKeys);
    }
  };
  useEffect(() => {
    if (tableColumns && data) {
      const distinctBy = (prop: any, arr: any) => {
        const uniqueValues = [...new Set(arr.map((o: any) => o[prop]))];
        return uniqueValues.sort((a, b) => {
          if (a === true) return -1;
          if (b === true) return 1;
          if (a === false) return 1;
          if (b === false) return -1;
          return 0;
        });
      };

      let filters: any = {};
      for (let i = 0; i < tableColumns.length; i++) {
        let result = distinctBy(tableColumns[i].key, data);
        filters[tableColumns[i].key] = result.map((value: any, index: any) => ({
          key: index,
          title: value === true ? "true" : value === false ? "false" : value ?? "",
        }));
      }
      setFilters(filters);
    }
  }, [tableColumns, data]);

  useEffect(() => {
    if (selectedRows) {
      const keys = selectedRows.map((x:any) => x.key);
      setSelectedRowKeys(keys);
    }
  }, [selectedRows]);

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    hideSelectAll: true,
  };

  
  return (
    <Table
      tableLayout="fixed"
      scroll={scroll}
      locale={locale}
      sticky={sticky}
      style={{ width: "100%" }}
      size="small"
      columns={columns}
      dataSource={data}
      pagination={paginationDisabled ? false : paginationConfig}
      rowClassName={(record) => (record.color ?? "") + (record.isSystemField ? " row-system-field" : "")}
      rowSelection={selectable ? rowSelection : undefined}
      loading={isTableLoading}
      onRow={(record, rowIndex) => {
        return {
          onDoubleClick: (event) => {
            onDoubleClickRow(record);
          },
        };
      }}
    />
  );
};

export default Datatable;
